import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from 'components/Button';
import CustomStarRating from 'components/CustomStarRating';
import FilterChipGroup from 'components/Find/FilterChipGroup';

export interface ReviewFormValues {
  rating: number;
  review: string;
  /** Undefined when the question isn't asked, or asked and left unanswered. */
  isSkillLevelAccurate?: boolean;
}

interface Props {
  /** Avatar on the coach flow, star illustration on the player flow. */
  media: ReactNode;
  heading: string;
  fieldLabel?: string;
  placeholder: string;
  /** Player reviews ask whether the subject's self-rated level matches reality. */
  showSkillAccuracy?: boolean;
  skillAccuracyLabel?: string;
  isSubmitting?: boolean;
  onSubmit: (values: ReviewFormValues) => void;
}

const RATING_LABELS = ['Poor', 'Mediocre', 'Average', 'Good', 'Amazing'];

const ACCURACY_OPTIONS = [
  { label: 'Yes', value: 'yes' as const },
  { label: 'No', value: 'no' as const },
];

export default function ReviewForm({
  media,
  heading,
  fieldLabel = 'Leave a public review',
  placeholder,
  showSkillAccuracy = false,
  skillAccuracyLabel = 'Is their skill level accurate?',
  isSubmitting = false,
  onSubmit,
}: Props) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReviewFormValues>({
    defaultValues: { rating: 5, review: '' },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col items-center px-ds-lg sm:px-ds-2xl">
        {media}
        <h3 className="typography-product-heading my-6">{heading}</h3>

        <Controller
          name="rating"
          control={control}
          rules={{ required: 'Rating is required' }}
          render={({ field }) => (
            <CustomStarRating
              fillColorClassName="text-color-brand-primary"
              unfillColorClassName="text-color-bg-lightmode-secondary"
              starClassName="w-10 h-10"
              starCount={5}
              textClassName="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary typography-product-element-label"
              gapBetweenStars="5"
              labels={RATING_LABELS}
              onRatingChange={field.onChange}
              initialRating={5}
            />
          )}
        />
        {errors.rating && <p className="text-red-500">{errors.rating.message}</p>}

        {showSkillAccuracy && (
          <div className="mt-ds-2xl w-full">
            <label className="typography-product-subheading">{skillAccuracyLabel}</label>
            <Controller
              name="isSkillLevelAccurate"
              control={control}
              render={({ field }) => (
                <div className="mt-3">
                  <FilterChipGroup
                    options={ACCURACY_OPTIONS}
                    selected={field.value == null ? [] : [field.value ? 'yes' : 'no']}
                    onToggle={(value) => field.onChange(value === 'yes')}
                    tone="solid"
                    layout="fill"
                  />
                </div>
              )}
            />
          </div>
        )}

        <div className="mt-ds-2xl w-full">
          <label className="typography-product-subheading" htmlFor="review-field">
            {fieldLabel}
          </label>
          <Controller
            name="review"
            control={control}
            rules={{ required: 'Review is required' }}
            render={({ field }) => (
              <textarea
                {...field}
                id="review-field"
                placeholder={placeholder}
                className="mt-3 h-[7.5rem] w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
              />
            )}
          />
          {errors.review && <p className="text-red-500">{errors.review.message}</p>}
        </div>
      </div>

      <div className="mt-ds-2xl flex flex-row-reverse border-t px-ds-lg py-ds-xl sm:mt-14 sm:px-ds-2xl">
        <Button
          size="lg"
          variant="primary"
          className="w-full sm:w-fit"
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </form>
  );
}
